import { Shield, Sparkles, Zap, ArrowRight } from "lucide-react";
import heroImage from "@/assets/hero-image-new.png";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

export const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero">
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-brand-pink/20 rounded-full blur-[120px] animate-blob" />
        <div className="absolute top-40 right-10 w-96 h-96 bg-brand-cyan/20 rounded-full blur-[120px] animate-blob animation-delay-2000" />
        <div className="absolute bottom-20 left-1/3 w-96 h-96 bg-brand-orange/20 rounded-full blur-[120px] animate-blob animation-delay-4000" />
      </div>
      
      {/* Radial Gradient Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-glow/30 via-transparent to-transparent" />
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      
      <div className="container relative z-10 px-4 py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8">
            {/* Badge with Animation */}
            <div 
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass-card-strong shadow-lg transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <Sparkles className="w-4 h-4 text-brand-orange animate-pulse-glow" />
              <span className="text-sm font-semibold text-primary-foreground">The AI assistant for Outlook</span>
            </div>
            
            {/* Main Headline with Staggered Animation */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-primary-foreground leading-tight space-y-2">
              <span 
                className={`block transition-all duration-700 delay-100 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                From inbox chaos
              </span>
              <span 
                className={`block bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent animate-gradient-shift transition-all duration-700 delay-200 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                to contact clarity.
              </span>
            </h1>
            
            {/* Subheadline */}
            <p 
              className={`text-xl md:text-2xl text-primary-foreground/90 max-w-2xl leading-relaxed transition-all duration-700 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              The AI assistant that transforms emails into structured contact intelligence — without complex CRM systems.
            </p>
            
            <p 
              className={`text-lg text-primary-foreground/75 max-w-2xl transition-all duration-700 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Works inside Outlook. No setup. No extra software.
            </p>
            
            {/* CTA Buttons */}
            <div 
              className={`flex flex-col gap-6 transition-all duration-700 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md mx-auto lg:mx-0">
                <Button 
                  size="lg" 
                  asChild 
                  className="flex-1 bg-white text-primary hover:bg-white/90 shadow-elevated hover:shadow-glow transition-all duration-300 hover:-translate-y-1 group"
                >
                  <a href="#waitlist" className="flex items-center justify-center gap-2">
                    Join Waitlist
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button 
                  size="lg" 
                  variant="ghost" 
                  asChild 
                  className="flex-1 glass-card-strong text-primary-foreground hover:bg-white/20 border-white/30 hover:border-white/50 transition-all duration-300"
                >
                  <a href="#how-it-works">See it in action</a>
                </Button>
              </div>
              
              {/* Feature Pills */}
              <div 
                className="flex flex-wrap items-center gap-3 md:gap-4 justify-center lg:justify-start text-primary-foreground/80 text-sm"
              >
                <div className="flex items-center gap-2 px-4 py-2 glass-card rounded-full hover:glass-card-strong transition-all duration-300 hover-lift">
                  <Sparkles className="w-4 h-4 text-brand-orange" />
                  <span>Works inside Outlook</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 glass-card rounded-full hover:glass-card-strong transition-all duration-300 hover-lift">
                  <Zap className="w-4 h-4 text-brand-cyan" />
                  <span>Credit-based pricing</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 glass-card rounded-full hover:glass-card-strong transition-all duration-300 hover-lift">
                  <Shield className="w-4 h-4 text-brand-pink" />
                  <span>Privacy-First</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Content - Hero Image */}
          <div 
            className={`relative perspective-1000 transition-all duration-1000 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
            }`}
          >
            {/* Glow Effect Behind Image */}
            <div className="absolute inset-0 bg-gradient-to-tr from-brand-pink/30 via-brand-orange/30 to-brand-cyan/30 rounded-3xl blur-[100px] animate-pulse-glow" />
            
            {/* Floating Border Frame */}
            <div className="absolute -inset-4 bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan rounded-3xl opacity-20 blur-lg animate-float" />
            
            {/* Main Image Card */}
            <div className="relative glass-card-strong rounded-2xl p-2 hover-glow">
              <img 
                src={heroImage} 
                alt="Outlook add contact from email signature automatically with Email2Contact AI-powered add-in"
                className="relative rounded-xl shadow-elevated w-full lg:max-w-xl xl:max-w-2xl transform hover:scale-[1.02] transition-transform duration-500"
              />
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-brand-pink/20 rounded-full blur-2xl animate-blob" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-brand-cyan/20 rounded-full blur-2xl animate-blob animation-delay-2000" />
          </div>
        </div>
      </div>
      
      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background via-background/80 to-transparent" />
    </section>
  );
};
